'use client'

import Image from 'next/image'
import React, { useEffect, useRef, useState } from 'react'

interface LazyImageProps {
    src: string
    alt: string
    sizes: string
    objectFit?: 'cover' | 'fill' | 'contain' | 'none' | 'scale-down'
    priority?: boolean
}

const LazyImage: React.FC<LazyImageProps> = ({ src, alt, sizes, objectFit = 'cover', priority = false }) => {
    // For priority images, always render immediately (no lazy loading)
    const [isInView, setIsInView] = useState(priority)
    const imgRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        // Skip IntersectionObserver for priority images - they should load immediately
        if (priority) return

        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setIsInView(true)
                        observer.disconnect()
                    }
                })
            },
            {
                rootMargin: '50px'
            }
        )

        if (imgRef.current) {
            observer.observe(imgRef.current)
        }

        return () => {
            observer.disconnect()
        }
    }, [priority])

    // For priority images, render the image immediately with eager loading
    // This ensures the image is in the SSR HTML and loads as fast as possible
    if (priority) {
        return (
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <Image
                    src={src}
                    alt={alt}
                    fill
                    loading="eager"
                    priority={true}
                    sizes={sizes}
                    style={{ objectFit }}
                    fetchPriority="high"
                />
            </div>
        )
    }

    // For non-priority images, use lazy loading with IntersectionObserver
    return (
        <div ref={imgRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
            {isInView ? (
                <Image
                    src={src}
                    alt={alt}
                    fill
                    loading="lazy"
                    sizes={sizes}
                    style={{ objectFit }}
                    fetchPriority="low"
                />
            ) : (
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#f3f4f6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                />
            )}
        </div>
    )
}

export default LazyImage
